/**
 * GENERATED_ASSET — revision-02 placement math (supersedes candidate1 src/transforms.ts).
 *
 * TF-04: transformBounds maps ALL FOUR corners, so it is correct for the whole SVG affine family the exported
 *        Matrix6 describes (flips, rotation, shear, singular scale). Singular matrices are allowed and give a
 *        zero-area box. Zero-size bounds are valid input; negative or non-finite bounds are rejected.
 * TF-05: every input-facing entry point validates finiteness and types with field paths BEFORE any math or SVG
 *        serialization; strings, booleans and null are never coerced. Results are re-checked for overflow.
 *
 * Standalone math: nothing here is wired into the canonical nature owner (see adapter.ts for the projection that is).
 */
import type { FountainArtifact, Point2, VisualAnchor, VisualBounds } from './types';
import { bounds as checkBounds, matrix as checkMatrix, finiteSvgNumber, optionalBoolean, point as checkPoint, positiveScale, throwIfIssues } from './guard';
import type { InputIssue } from './guard';

/** SVG-style matrix [a b c d e f]: x' = a*x + c*y + e, y' = b*x + d*y + f. */
export type Matrix6 = readonly [number, number, number, number, number, number];

export interface Placement {
  /** World point where the PAINTED base contact (base_contact) should rest. */
  ground: Point2;
  scale: number;
  flipX?: boolean;
}

/** Throws InputValidationError naming the field path of every non-finite / wrong-typed component. */
export function assertMatrix(m: unknown, path = 'matrix'): asserts m is Matrix6 {
  const issues: InputIssue[] = [];
  checkMatrix(issues, path, m);
  throwIfIssues('transform matrix', issues);
}

function checked(m: readonly number[], context: string): Matrix6 {
  const issues: InputIssue[] = [];
  checkMatrix(issues, 'result', m);
  throwIfIssues(`${context} result (numeric overflow)`, issues);
  return m as unknown as Matrix6;
}

export function placeByContact(art: Pick<FountainArtifact, 'support'>, pl: Placement): Matrix6 {
  const issues: InputIssue[] = [];
  checkPoint(issues, 'artifact.support.point', art?.support?.point);
  if (typeof pl !== 'object' || pl === null) issues.push({ path: 'placement', message: 'must be an object {ground, scale, flipX?}' });
  else {
    checkPoint(issues, 'placement.ground', pl.ground);
    positiveScale(issues, 'placement.scale', pl.scale);
    optionalBoolean(issues, 'placement.flipX', pl.flipX);
  }
  throwIfIssues('placement (placeByContact)', issues);
  const sx = pl.flipX ? -1 : 1, c = art.support.point;
  return checked([pl.scale * sx, 0, 0, pl.scale, pl.ground.x - pl.scale * sx * c.x, pl.ground.y - pl.scale * c.y], 'placeByContact');
}

/** Place by the frame's bottom-center origin instead (what a frame-based texture owner does). */
export function placeByOrigin(art: Pick<FountainArtifact, 'origin'>, world: Point2, scale: number, flipX?: boolean): Matrix6 {
  const issues: InputIssue[] = [];
  checkPoint(issues, 'artifact.origin.point', art?.origin?.point);
  checkPoint(issues, 'world', world);
  positiveScale(issues, 'scale', scale);
  optionalBoolean(issues, 'flipX', flipX);
  throwIfIssues('placement (placeByOrigin)', issues);
  const sx = flipX ? -1 : 1, o = art.origin.point;
  return checked([scale * sx, 0, 0, scale, world.x - scale * sx * o.x, world.y - scale * o.y], 'placeByOrigin');
}

export function transformPoint(m: Matrix6, p: Point2): Point2 {
  const issues: InputIssue[] = [];
  checkMatrix(issues, 'matrix', m);
  checkPoint(issues, 'point', p);
  throwIfIssues('transformPoint input', issues);
  const out = [m[0] * p.x + m[2] * p.y + m[4], m[1] * p.x + m[3] * p.y + m[5]];
  checked([...out, 0, 0, 0, 0], 'transformPoint');
  return { x: out[0]!, y: out[1]! };
}

/** Axis-aligned bounds of the transformed box: min/max over all four transformed corners. */
export function transformBounds(m: Matrix6, b: VisualBounds): VisualBounds {
  const issues: InputIssue[] = [];
  checkMatrix(issues, 'matrix', m);
  checkBounds(issues, 'bounds', b);
  throwIfIssues('transformBounds input', issues);
  const xs: number[] = [], ys: number[] = [];
  for (const [cx, cy] of [[b.x, b.y], [b.x + b.width, b.y], [b.x + b.width, b.y + b.height], [b.x, b.y + b.height]] as const) {
    xs.push(m[0] * cx + m[2] * cy + m[4]);
    ys.push(m[1] * cx + m[3] * cy + m[5]);
  }
  const x = Math.min(...xs), y = Math.min(...ys);
  const out = { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
  checked([out.x, out.y, out.width, out.height, 0, 0], 'transformBounds');
  return out;
}

export function transformAnchors(art: Pick<FountainArtifact, 'anchors'>, m: Matrix6): VisualAnchor[] {
  const issues: InputIssue[] = [];
  checkMatrix(issues, 'matrix', m);
  if (!Array.isArray(art?.anchors)) issues.push({ path: 'artifact.anchors', message: 'must be an array' });
  else art.anchors.forEach((a, i) => checkPoint(issues, `artifact.anchors[${i}].point`, a?.point));
  throwIfIssues('transformAnchors input', issues);
  return art.anchors.map((a) => ({ ...a, point: transformPoint(m, a.point) }));
}

/** SVG attribute value; refuses to serialize a matrix with any non-finite component. */
export function matrixAttr(m: Matrix6): string {
  assertMatrix(m);
  return `matrix(${m.map((v, i) => finiteSvgNumber(v, `matrix[${i}]`)).join(' ')})`;
}

export function findAnchor(art: Pick<FountainArtifact, 'anchors'>, name: string): Point2 {
  const a = art.anchors.find((x) => x.name === name);
  if (!a) throw new Error(`Unknown anchor ${JSON.stringify(name)}. Available: ${art.anchors.map((x) => x.name).join(', ')}`);
  return a.point;
}
